import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Divider,
} from '@mui/material';
import {
  History as HistoryIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  AccountCircle as AdminIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface AuditLog {
  _id: string;
  entityType: 'user' | 'challan';
  entityId: string;
  action: string;
  changes: Record<string, { old: any; new: any }>;
  adminUser: string;
  reason?: string;
  timestamp: string;
  createdAt: string;
}

interface AuditLogResponse {
  data: AuditLog[];
  pagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  };
}

function AuditLogs() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [adminFilter, setAdminFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce search input changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(0);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  useEffect(() => {
    fetchAuditLogs();
  }, [page, rowsPerPage, entityTypeFilter, adminFilter, searchQuery]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (entityTypeFilter) params.entityType = entityTypeFilter;
      if (adminFilter) params.adminUser = adminFilter;
      if (searchQuery) params.searchQuery = searchQuery;

      const response = await api.get('/audit-logs', { params });
      const data: AuditLogResponse = response.data;
      setLogs(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatChanges = (changes: Record<string, { old: any; new: any }>) => {
    return Object.entries(changes).map(([field, change]) => ({
      field,
      old: typeof change.old === 'object' ? JSON.stringify(change.old) : String(change.old),
      new: typeof change.new === 'object' ? JSON.stringify(change.new) : String(change.new)
    }));
  };

  const clearFilters = () => {
    setEntityTypeFilter('');
    setAdminFilter('');
    setSearchInput('');
    setSearchQuery('');
    setPage(0);
  };

  const getEntityIcon = (entityType: string) => {
    return entityType === 'user' ? <PersonIcon /> : <ReceiptIcon />;
  };

  const getEntityColor = (entityType: string) => {
    return entityType === 'user' ? 'primary' : 'secondary';
  };

  // Get unique admin users for filter
  const uniqueAdmins = Array.from(new Set(logs.map(log => log.adminUser)));

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <HistoryIcon sx={{ mr: 2, fontSize: 40 }} />
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Audit Logs
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Complete history of all changes made to users and challans
          </Typography>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Changes
                </Typography>
                <Typography variant="h5" color="primary.main">
                  {total.toLocaleString()}
                </Typography>
              </Box>
              <HistoryIcon color="primary" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  User Changes
                </Typography>
                <Typography variant="h5" color="info.main">
                  {logs.filter(log => log.entityType === 'user').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current page
                </Typography>
              </Box>
              <PersonIcon color="info" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Challan Changes
                </Typography>
                <Typography variant="h5" color="secondary.main">
                  {logs.filter(log => log.entityType === 'challan').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current page
                </Typography>
              </Box>
              <ReceiptIcon color="secondary" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Filters</Typography>
        </Box>
        
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            sx={{ minWidth: 280 }}
            label="Search Challan ID / Phone / Email"
            variant="outlined"
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="e.g. 12345, user@email.com, 0300..."
          />

          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Entity Type</InputLabel>
            <Select
              value={entityTypeFilter}
              label="Entity Type"
              onChange={(e) => setEntityTypeFilter(e.target.value as string)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="user">Users</MenuItem>
              <MenuItem value="challan">Challans</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Admin User</InputLabel>
            <Select
              value={adminFilter}
              label="Admin User"
              onChange={(e) => setAdminFilter(e.target.value as string)}
            >
              <MenuItem value="">All</MenuItem>
              {uniqueAdmins.map((admin) => (
                <MenuItem key={admin} value={admin}>
                  {admin}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(entityTypeFilter || adminFilter || searchInput) && (
            <Chip
              label="Clear Filters"
              onClick={clearFilters}
              onDelete={clearFilters}
              color="primary"
            />
          )}
        </Box>
      </Paper>

      {/* Audit Logs Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Admin User</TableCell>
                <TableCell>Changes</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log._id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(log.timestamp)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getEntityIcon(log.entityType)}
                        <Box ml={1}>
                          <Chip
                            label={log.entityType}
                            color={getEntityColor(log.entityType) as any}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            ID: {log.entityId.slice(-8)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        label={log.action}
                        color="warning"
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <AdminIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight="medium">
                          {log.adminUser}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        {formatChanges(log.changes).map((change, index) => (
                          <Box key={index} mb={1}>
                            <Typography variant="body2" fontWeight="medium" color="primary">
                              {change.field}:
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={change.old || 'null'}
                                size="small"
                                variant="outlined"
                                color="error"
                                sx={{ fontSize: '0.7rem', maxWidth: 120 }}
                              />
                              <Typography variant="body2">→</Typography>
                              <Chip
                                label={change.new || 'null'}
                                size="small"
                                variant="outlined"
                                color="success"
                                sx={{ fontSize: '0.7rem', maxWidth: 120 }}
                              />
                            </Stack>
                          </Box>
                        ))}
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', maxWidth: 200 }}>
                        {log.reason || 'No reason provided'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>
    </Box>
  );
}

export default AuditLogs; 